import { deepClone } from '@/util/objects.js';
import { toVfId } from 'commons/util/parse.js';
import { NONE, PENDING_NEW, PENDING_UPDATE } from 'commons/constants/user_moderation.js';

export const parseV2 = (data) => {
    setBans(data);
    const clone = deepClone(data);

    // if the timestamp is not in milliseconds yet, convert it
    if (clone.date_created.toString().length < 13) {
        [ 'date_created', 'date_followed', 'date_following', 'date_muted' ].forEach((key) => {
            if (clone[key]) {
                clone[key] *= 1000;
            }
        });
    }
    clone.date = clone.date_created;
    return clone;
};

const emailVerificationStatus = ({ email_pending, is_new_user }) => {
    if (!email_pending) {
        return NONE;
    }
    return is_new_user ? PENDING_NEW : PENDING_UPDATE;
};

const displayName = (preferred, currentUsername) => preferred || currentUsername.name || '';

const usernameModerationStatus = (pendingUsername, currentUsername) =>
    pendingUsername.moderation_status || currentUsername.moderation_status || NONE;

// auth-service V3 returns only currently-active bans, each as { section_uuid, level }. Map every entry
// onto the shape the moderation banned-badge consumes: a numeric per-site site_id and a ban_level.
// permanent is set so the shared isBanActive gate passes — the service has already filtered to active,
// and the V3 payload carries no client-visible expiry to evaluate.
// site_id is only derived when section_uuid is present: a ban missing it (e.g. an auth-service that
// predates section_uuid during a staggered rollout) must not crash parseV3 and break the whole queue —
// it simply won't match a site, so no badge renders, matching the pre-fix behaviour.
const mapActiveBans = (activeBans) => {
    const list = Array.isArray(activeBans) ? activeBans : Object.values(activeBans || {});
    return list.map((ban) => ({
        ...ban,
        site_id: ban.section_uuid ? toVfId(ban.section_uuid) : undefined,
        ban_level: ban.level,
        permanent: true,
    }));
};

const BAN_STATUS_ACTIVE = 'ACTIVE';

// Maps a ban-history entry (GET /v3/sections/{section}/users/{user}/bans) onto the same shape, so the
// shared isBanActive gate stays the single arbiter of what counts as active. expires_at is in epoch
// seconds, matching end_date's unit. manual_expiry carries the service's derived status for a ban ended
// before its natural expiry — without it, a ban a moderator revoked early would still read as active.
const mapBanHistory = (banHistory) => banHistory.map((ban) => {
    // permanent already means "expires_at is null or absent", so reuse it rather than testing expires_at for
    // falsiness — an expires_at of 0 is a real epoch value, not a missing one.
    const permanent = ban.expires_at === null || ban.expires_at === undefined;
    const end_date = permanent ? 0 : ban.expires_at;
    const expiredNaturally = !permanent && Date.now() >= end_date * 1000;

    return {
        ...ban,
        site_id: ban.section_uuid ? toVfId(ban.section_uuid) : undefined,
        ban_level: ban.level,
        permanent,
        end_date,
        manual_expiry: ban.status !== BAN_STATUS_ACTIVE && !expiredNaturally,
    };
});

const dedupeBans = (bans) => {
    const seen = new Set();
    return bans.filter(({ ban_uuid }) => {
        if (!ban_uuid) {
            return true;
        }
        if (seen.has(ban_uuid)) {
            return false;
        }
        seen.add(ban_uuid);
        return true;
    });
};

// Only a response we actually understand may assert "this user has no bans", because parseV3 treats that
// as authoritative and stops consulting active_bans. Anything unrecognised returns null — the same marker a
// failed read uses — so it degrades to active_bans instead of silently erasing an active ban. The spec
// returns BanHistoryResponseV3 ({ size, payload }); a bare array is accepted for the same safety reason.
export const banHistoryPayload = (response) => {
    if (Array.isArray(response)) {
        return response;
    }
    if (response && Array.isArray(response.payload)) {
        return response.payload;
    }
    return null;
};

// Merges the per-section ban-history reads, in lineage order: histories[0] is the section under moderation
// and the rest are its ancestors. A read that failed arrives as null.
//
// Only the primary read can speak for the current site, so if it failed there is no authoritative answer to
// give and this returns undefined, which makes parseV3 fall back to active_bans. Returning what the ancestors
// said would be worse than useless: an ancestor that legitimately holds no bans would answer with an empty
// history, parseV3 would take that as "this user has no bans", and the current site's active-ban badge would
// disappear for a user who is in fact banned — a worse regression than the missing history this change fixes.
export const mergeBanHistories = (histories) => {
    const [ primary ] = histories;
    if (!Array.isArray(primary)) {
        return undefined;
    }
    return dedupeBans(histories.filter(Array.isArray).flat());
};

export const parseV3 = (data) => {
    const clone = deepClone(data);
    // ban_history is attached by the fetch, not part of the V3 user payload — keep it out of the store record.
    const banHistory = clone.ban_history;
    delete clone.ban_history;
    const uuid = clone.uuid || clone.sub;
    const username_info = clone.username_info || {};
    const username_pending_info = clone.username_pending_info || {};
    const picture = clone.picture || '';
    // Ban history supersedes active_bans when it was fetched: it is the superset, and total_bans is the
    // historic count the "Bans: N" indication has always shown. active_bans remains the fallback for
    // callers that have no ban history (no moderator scope on any section in the lineage, or a failed read).
    const bans = Array.isArray(banHistory) ? mapBanHistory(banHistory) : mapActiveBans(clone.active_bans);
    const date_created = typeof clone.created_at === 'number' ? clone.created_at * 1000 : 0;

    return {
        ...clone,
        uuid,
        id: uuid ? toVfId(uuid) : clone.id,
        name: displayName(clone.preferred_username, username_info),
        username_pending: username_pending_info.name || '',
        username_moderation_status: usernameModerationStatus(username_pending_info, username_info),
        pic_large: picture,
        pic_small: picture,
        email: clone.email || '',
        email_pending: clone.email_pending || '',
        email_verification_status: emailVerificationStatus(clone),
        site_uuid: clone.site_uuid || clone.section_uuid,
        bans,
        total_bans: bans.length,
        date_created,
        date: date_created,
    };
};

const setBans = (data) => {
    data.bans = Object.values(data.bans || {});
    const total_bans = data.bans.length;

    data.total_bans = total_bans;
};
